import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  LayoutGrid,
  Pin,
  PinOff,
  Plus,
  Undo2,
} from "lucide-react";
import { SablonaInsertDialog } from "@/components/sablona/SablonaInsertDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useUpdateZakazka, useZakazkaDetail } from "@/api/hooks/useZakazky";
import { useCreateVarianta, useDeleteVarianta } from "@/api/hooks/useVarianty";
import { useDeleteDielec, useQuickCreateDielec } from "@/api/hooks/useDielce";
import { useHromadneZostavy } from "@/api/hooks/useHromadneZostavy";
import {
  useDeleteZostava,
  useDuplicateZostava,
  usePoradieDielcov,
  usePoradieZostav,
} from "@/api/hooks/useZostavy";
import {
  poradieZostavy,
  posunOJeden,
  rovnakePoradie,
} from "@/lib/poradieDielcov";
import { cn } from "@/lib/utils";
import { useEditorHotkeys, type EditorHotkeys } from "@/lib/useEditorHotkeys";
import { useHeartbeat } from "@/lib/useHeartbeat";
import { UndoProvider, useUndo } from "@/lib/undo/UndoContext";
import { useImport } from "@/lib/import/ImportContext";
import { SkratkyDialog } from "@/components/zakazka/SkratkyDialog";
import { CenaHistoriaDialog } from "@/components/zakazka/CenaHistoriaDialog";
import { PorovnanieDialog } from "@/components/zakazka/PorovnanieDialog";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { pismena, suggestNextKod } from "@/lib/dielecKod";
import { ZakazkaHeader } from "@/components/zakazka/ZakazkaHeader";
import { ListaOdborov } from "@/components/zakazka/ListaOdborov";
import { KapsuleVerzii } from "@/components/zakazka/KapsuleVerzii";
import { PasRegalNacenit } from "@/components/zakazka/PasRegalNacenit";
import { ZakazkaFlatTable } from "@/components/zakazka/ZakazkaFlatTable";
import { BocnyPanel } from "@/components/zakazka/BocnyPanel";
import { PridatMenu, PridatTovarMenu } from "@/components/zakazka/PridatMenu";
import { AkcieVerzieMenu, AkcieZakazkyMenu } from "@/components/zakazka/AkcieZakazky";
import { useSlotyZMaterialov } from "@/api/hooks/useSlotyZMaterialov";
import { OpravyDialog } from "@/components/import/OpravyDialog";
import { VystupyMenu } from "@/components/zakazka/VystupyMenu";
import { jeTovarovaVarianta } from "@/lib/variantyZakazky";
import {
  cielOdboru,
  jeVCene,
  odborVarianty,
  variantyOdboru,
  type OdborZakazky,
} from "@/lib/odboryZakazky";
import { TovarVarianta } from "@/components/tovar/TovarVarianta";
import { DielecCreateDialog } from "@/components/dielec/DielecCreateDialog";
import { VlastnaPolozkaDialog } from "@/components/dielec/VlastnaPolozkaDialog";
import { DielecDetailPanel } from "@/components/dielec/DielecDetailPanel";
import { HromadnaUpravaPanel } from "@/components/dielec/HromadnaUpravaPanel";
import { PasHran } from "@/components/zakazka/PasHran";
import { PasCutrite } from "@/components/zakazka/PasCutrite";
import { HranyLista } from "@/components/dielec/HranyLista";
import { ZostavaDetailPanel } from "@/components/zostava/ZostavaDetailPanel";
import { ZostavaCreateDialog } from "@/components/zostava/ZostavaCreateDialog";
import { NovyCelokDialog } from "@/components/zakazka/Celky";
import { susedVSkupine } from "@/lib/celky";
import { useVytvorCelok } from "@/api/hooks/useCelky";
import { AutoSaveIndicator } from "@/components/shared/AutoSaveIndicator";
import { KomponentDialog } from "@/components/komponent/KomponentDialog";
import { getLastSlot } from "@/lib/lastSlot";
import {
  adresaZoznamu,
  zabudniZakazku,
  zapamatajZakazku,
} from "@/lib/zoznamZakaziek";
import {
  klucOdboru,
  klucVarianty,
  nacitajVariantu,
  pociatocnaVarianta,
  ulozVariantu,
} from "@/lib/poslednaVarianta";
import { useMe, usePrava } from "@/api/hooks/useAuth";
import { rezimVarianty, zamokVarianty } from "@/lib/prava";
import { SkloCreateDialog } from "@/components/sklo/SkloCreateDialog";
import {
  VariantaCreateDialog,
  type RezimNovejVarianty,
} from "@/components/varianta/VariantaCreateDialog";
import {
  NAZOV_PRIPOJENIA,
  POPIS_PRIPOJENIA,
  PripojitDialog,
  stranyPripojenia,
  type StranaPripojenia,
} from "@/components/zakazka/PripojitDialog";
import { PremenovatVariantuDialog } from "@/components/varianta/PremenovatVariantuDialog";
import { TopSolidDoZakazkyDialog } from "@/components/varianta/TopSolidDoZakazkyDialog";
import type {
  Celok,
  Dielec,
  DielecCreate,
  MaterialSlot,
  Varianta,
  ZakazkaDetail,
  Zostava,
} from "@/api/schemas";
import { OPERACIA_LEPENA_VRSTVA, jeOperaciaRiadok } from "@/lib/operacie";
import { useAkcieSchranky } from "@/lib/schrankaRiadkov";
import { sklonuj } from "@/lib/format";
import { riadkyNaOznacenie, type CielOznacenia } from "@/lib/oznacenieRiadkov";

/**
 * Editor zákazky — R1-FINAL Round 2 workflow:
 *
 *   ┌─ Header (back, číslo, klient, stav, varianty tabs) ──┐
 *   │                                                       │
 *   ├─ Toolbar [+ Zostava, …]   AutoSaveIndicator           │
 *   │                                                       │
 *   ├─ Pás detailu — NAD mriežkou, cez plnú šírku, pripnutý │
 *   │  zvrchu (KOLO 64). Hrany sa klikajú tu a inde sa      │
 *   │  zmeniť nedajú, takže musí byť stále na očiach.       │
 *   │                                                       │
 *   ├─ FlatTable                Sloty + Summary             │
 *   │  • Zostavy expandable     • M1, M2, …                 │
 *   │  • Dielce indented        • Cena celkom               │
 *   │  • Inline edit Ks/X/Y/Z   • taby Sloty/Hrany/…        │
 *   │  • Klik = select                                      │
 *   └───────────────────────────────────────────────────────┘
 *
 * Inšpirácia: DaexCUT screenshoty 04 (zostavy) + 08 (detail).
 */
/**
 * Voľba „zobraziť detail dielca" — prežije reload aj prepnutie zákazky.
 *
 * Nový kľúč, nie recyklovaný `tandem_detail_pripnuty`: tam `"0"` znamenalo
 * „nepripnutý, ale zobrazený", takže by sa detail skryl ľuďom, ktorí ho mať
 * chceli. Skrytie dá tabuľke zhruba dvojnásobok riadkov (KOLO 56).
 */
const LS_DETAIL_VIDITELNY = "tandem_detail_viditelny";

/**
 * Voľba „pripnúť pás detailu" (KOLO 66) — pripnutý pri scrollovaní stojí,
 * odopnutý odíde s obsahom. Default je **pripnutý**, teda stav z KOLA 64:
 * kto neklikne, nič sa mu nezmení.
 *
 * Je to `localStorage`, nie nastavenie firmy — „kolega si vyberie čo chce"
 * znamená per prehliadač, nie jednu hodnotu pre všetkých.
 *
 * A opäť **nový kľúč**, nie recyklovaný `tandem_detail_pripnuty` z KOLA 48:
 * tam by staré `"0"` odopnulo pás ľuďom, ktorí ho vtedy odpichli v úplne
 * inom layoute, a default „ako doteraz" by tichým spôsobom neplatil.
 */
const LS_PAS_PRIPNUTY = "tandem_pas_pripnuty";

export function ZakazkaEditorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const zakazkaQuery = useZakazkaDetail(id);

  // Zákazky v menu sa sem vrátia z Cenníka a iných stránok (KOLO 215);
  // zákazka, ktorá sa nedá načítať, sa zabudne
  const nacitana = zakazkaQuery.data !== undefined;
  useEffect(() => {
    if (!id) return;
    if (zakazkaQuery.isError) zabudniZakazku();
    else if (nacitana) zapamatajZakazku(id);
  }, [id, nacitana, zakazkaQuery.isError]);

  if (zakazkaQuery.isLoading) {
    return <LoadingSkeleton />;
  }
  if (zakazkaQuery.isError) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="rounded-lg border border-destructive bg-destructive/10 p-6">
          <p className="font-medium text-destructive">
            Zákazka sa nedá načítať
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {zakazkaQuery.error?.message}
          </p>
        </div>
      </div>
    );
  }
  if (!zakazkaQuery.data || !id) {
    return <BackLink />;
  }

  return (
    // Provider musí byť **nad** EditorBody — mutačné hooky vnútri doň
    // zapisujú kroky. Zásobník je per zákazka, preto `zakazkaId`.
    <UndoProvider zakazkaId={id}>
      {/* `key` — prechod na inú zákazku začne nanovo; inak by si editor
          niesol záložku predošlej a uložil ju k novej (KOLO 215) */}
      <EditorBody
        key={id}
        zakazkaId={id}
        zakazka={zakazkaQuery.data.data as ZakazkaDetail}
      />
    </UndoProvider>
  );
}

function EditorBody({
  zakazkaId,
  zakazka,
}: {
  zakazkaId: string;
  zakazka: ZakazkaDetail;
}): JSX.Element {
  const varianty = (zakazka.varianty ?? []) as Varianta[];
  const navigate = useNavigate();

  // Záložka, na ktorej user naposledy pracoval (KOLO 215) — pamäť
  // prehliadača pre používateľa a zákazku, aktívnu variantu nemení
  const pouzivatel = useMe().data?.data.user?.username ?? "";
  const klucZalozky = klucVarianty(pouzivatel, zakazkaId);
  // Konštruktér otvára zákazku prvý raz na výrobe (KOLO 240) — výpočty
  // a tovar len číta; ostatní na výpočte v cene ako doteraz
  const jeKonstrukter = usePrava().rola === "konstrukter";
  const [activeVariantaId, setActiveVariantaId] = useState<string | null>(() =>
    pociatocnaVarianta(
      nacitajVariantu(klucZalozky),
      varianty,
      (jeKonstrukter ? variantyOdboru(varianty, "vyroba")[0]?.id : undefined) ??
        zakazka.aktivna_varianta_id,
    ),
  );
  useEffect(() => {
    if (!activeVariantaId) return;
    ulozVariantu(klucZalozky, activeVariantaId);
    // Posledná verzia v odbore — klik na odbor v lište sa na ňu vráti
    const v = varianty.find((x) => x.id === activeVariantaId);
    if (v) ulozVariantu(klucOdboru(pouzivatel, zakazkaId, odborVarianty(v)), v.id);
  }, [klucZalozky, activeVariantaId, varianty, pouzivatel, zakazkaId]);
  const [selectedDielecId, setSelectedDielecId] = useState<string | null>(null);
  // Klik na riadok zostavy → detail zostavy (marža produktu). Výber
  // zostavy a dielca sa vzájomne vylučujú.
  const [vybranaZostavaId, setVybranaZostavaId] = useState<string | null>(null);
  // Čerstvo quick-add-nutý dielec — tabuľka mu otvorí editáciu názvu
  const [focusDielecId, setFocusDielecId] = useState<string | null>(null);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [rezimNovej, setRezimNovej] = useState<RezimNovejVarianty>("vypocet");
  const [pripojitStrana, setPripojitStrana] = useState<StranaPripojenia | null>(null);
  // Rozpočet tovaru (KOLO 241) — Regál CRC a Pridať ▾ sú v paneli editora,
  // okná tovaru riadi editor; vybraná zostava tovaru je cieľ Pridať ▾
  const [regalTovaru, setRegalTovaru] = useState<string | null>(null);
  const [pridatTovarDo, setPridatTovarDo] = useState<Zostava | null>(null);
  const [zostavaTovaruId, setZostavaTovaruId] = useState<string | null>(null);
  const [topsolidDialogOpen, setTopsolidDialogOpen] = useState(false);
  const [zostavaDialogOpen, setZostavaDialogOpen] = useState(false);
  // Nová zostava pod túto (KOLO 196 — Pridať zostavu z menu zostavy);
  // `null` = na koniec ako horné tlačidlo
  const [zostavaPo, setZostavaPo] = useState<string | null>(null);
  // Nová zostava do celku (KOLO 235) — z menu celku alebo pri vybranej
  // zostave v celku
  const [zostavaDoCelku, setZostavaDoCelku] = useState<string | null>(null);
  const [novyCelokOpen, setNovyCelokOpen] = useState(false);
  const [dielecCreateContext, setDielecCreateContext] = useState<{
    zostavaId: string;
    zostavaKod: string;
    /** `vlastna` = vlastná položka mimo cenníka (KOLO 210), vlastné okno. */
    rezim: "kovanie" | "cenovka" | "vlastna";
    /** Ďalšie vybrané zostavy — ten istý riadok do každej (KOLO 196). */
    dalsie?: string[];
  } | null>(null);
  const [komponentDialogOpen, setKomponentDialogOpen] = useState(false);
  const [sablonaInsertOpen, setSablonaInsertOpen] = useState(false);
  const [skloDialogOpen, setSkloDialogOpen] = useState(false);
  // Edit mód kalkulačky skla — cieľový sklo dielec (z detail panelu)
  const [skloEditTarget, setSkloEditTarget] = useState<Dielec | null>(null);
  // Pás detailu nad mriežkou. Jediné, čo dáva zmysel prepínať, je či tam
  // je: skrytý uvoľní ~325 px, teda zhruba dvojnásobok riadkov kusovníka.
  const [detailViditelny, setDetailViditelny] = useState<boolean>(
    () => localStorage.getItem(LS_DETAIL_VIDITELNY) !== "0",
  );
  useEffect(() => {
    localStorage.setItem(LS_DETAIL_VIDITELNY, detailViditelny ? "1" : "0");
  }, [detailViditelny]);
  const [pasPripnuty, setPasPripnuty] = useState<boolean>(
    () => localStorage.getItem(LS_PAS_PRIPNUTY) !== "0",
  );
  useEffect(() => {
    localStorage.setItem(LS_PAS_PRIPNUTY, pasPripnuty ? "1" : "0");
  }, [pasPripnuty]);
  const pasRef = useRef<HTMLDivElement>(null);
  // Viacnásobný výber pre hromadnú úpravu. Rozsah (Shift+klik) počíta
  // tabuľka — pozná poradie riadkov aj zbalené zostavy; tu žije len výsledok,
  // lebo ho potrebuje pás detailu nad tabuľkou.
  const [vybraneDielce, setVybraneDielce] = useState<Set<string>>(new Set());
  // Viacvýber zostáv Ctrl/Shift+klikom na pás (KOLO 196) — user: „nech
  // Ctrl funguje aj pri zostavách… vložiť do nich rovnaké kovanie"
  const [vybraneZostavy, setVybraneZostavy] = useState<Set<string>>(new Set());
  // Označiť v tabuľke z panela (KOLO 233) — tabuľka rozbalí a posunie
  const [odhalit, setOdhalit] = useState<{
    dielce: string[];
    rozbalitDielce: string[];
    token: number;
  } | null>(null);

  // Je pás odscrollovaný z dohľadu? Vtedy (a len vtedy) má zmysel mini-lišta
  // hrán — 48 px, ktoré cestujú so scrollom namiesto 325 px pásu (KOLO 67).
  //
  // KOLO 66 to riešilo `scrollIntoView` na výber dielca, čo skákalo stránkou
  // a odtrhávalo riadok, na ktorý user klikol, od výhľadu. Lišta to nahrádza:
  // editor hrán príde za ním a tabuľka zostane tam, kde je.
  const [pasVidno, setPasVidno] = useState(true);
  useEffect(() => {
    const el = pasRef.current;
    // jsdom `IntersectionObserver` nemá (stub je v `test/setup.ts`); keď by
    // chýbal, radšej lištu nezobrazuj než ju zobraziť naveky.
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      ([zapis]) => setPasVidno(zapis?.isIntersecting ?? true),
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // `detailViditelny` je v deps, lebo pri skrytí pásu element zmizne
  }, [detailViditelny]);

  // Nová časť z importu sa otvorí vo svojej záložke (`?varianta=`, KOLO 203)
  const [hladanie, setHladanie] = useSearchParams();
  const variantaZAdresy = hladanie.get("varianta");
  useEffect(() => {
    if (!variantaZAdresy || !varianty.some((v) => v.id === variantaZAdresy))
      return;
    setActiveVariantaId(variantaZAdresy);
    setHladanie(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete("varianta");
        return n;
      },
      { replace: true },
    );
  }, [variantaZAdresy, varianty, setHladanie]);
  const [premenovat, setPremenovat] = useState<Varianta | null>(null);

  useEffect(() => {
    if (activeVariantaId && !varianty.some((v) => v.id === activeVariantaId)) {
      setActiveVariantaId(
        zakazka.aktivna_varianta_id ?? varianty[0]?.id ?? null,
      );
    }
  }, [varianty, zakazka.aktivna_varianta_id, activeVariantaId]);

  const activeVarianta = useMemo(
    () => varianty.find((v) => v.id === activeVariantaId) ?? null,
    [varianty, activeVariantaId],
  );

  /**
   * „Vytvoriť sloty z materiálov" — pre zákazky z importu spred KOLA 96.
   * Zoskupenie robí backend (tú istú službu volá aj import), tu sa len
   * povie, čo sa stalo; vrátiť sa to dá cez Vrátiť / Ctrl+Z.
   */
  const slotyMutation = useSlotyZMaterialov(
    zakazkaId,
    activeVarianta?.id ?? "",
  );
  async function spustiSlotyZMaterialov(): Promise<void> {
    if (!activeVarianta) return;
    try {
      const v = await slotyMutation.mutateAsync();
      if (v.prepnutych === 0) {
        toast({
          title: "Nie je čo prepnúť",
          description:
            "Dielce už materiál zákazky majú, alebo ide o kovanie a " +
            "pracovné dosky — tie ho nemajú.",
        });
        return;
      }
      toast({
        title: `Vytvorené materiály zákazky: ${v.vytvorene.length}`,
        description: `Prepnutých ${v.prepnutych} dielcov. Cena sa nemení.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Materiály zákazky sa nepodarilo vytvoriť",
        description: extractErrorMessage(err),
      });
    }
  }

  const deleteVarianta = useDeleteVarianta(zakazkaId);

  async function handleDeleteVarianta(): Promise<void> {
    if (!activeVarianta) return;
    const label = activeVarianta.je_vyrobna
      ? `výrobnú variantu ${activeVarianta.nazov}`
      : `výpočet ${activeVarianta.nazov}`;
    if (!window.confirm(`Zmazať ${label} vrátane jej zostáv a dielcov?`)) {
      return;
    }
    try {
      await deleteVarianta.mutateAsync(activeVarianta.id);
      const zvysne = varianty.filter((v) => v.id !== activeVarianta.id);
      setActiveVariantaId(zvysne[0]?.id ?? null);
      toast({
        title: activeVarianta.je_vyrobna
          ? "Varianta zmazaná"
          : "Výpočet zmazaný",
        description: label,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: activeVarianta.je_vyrobna
          ? "Variantu sa nepodarilo zmazať"
          : "Výpočet sa nepodarilo zmazať",
        description: extractErrorMessage(err),
      });
    }
  }

  // Odbor otvorenej verzie (KOLO 240) — lišta Nábytok · Tovar · Výroba,
  // kapsule pod ňou sú verzie tohto odboru v poradí záložiek (KOLO 206)
  const odbor: OdborZakazky | null = activeVarianta ? odborVarianty(activeVarianta) : null;
  const verzieOdboru = useMemo(
    () => (odbor ? variantyOdboru(varianty, odbor) : []),
    [varianty, odbor],
  );
  function prepniOdbor(o: OdborZakazky): void {
    const id = cielOdboru(
      zakazka,
      varianty,
      o,
      nacitajVariantu(klucOdboru(pouzivatel, zakazkaId, o)),
    );
    if (id) setActiveVariantaId(id);
  }
  const maVyrobnu = varianty.some((v) => v.je_vyrobna);
  // Rozpočet tovaru (KOLO 230) — regály CRC: vlastný pohľad, bez výroby
  const jeTovar = activeVarianta ? jeTovarovaVarianta(activeVarianta) : false;
  // Roly (KOLO 231): obchodník v nábytku mení len cenu (panel zostavy),
  // rozpočtár tovar len číta, konštruktér výpočty a tovar len číta a výrobu
  // mení celú (KOLO 239). `zamok` = prečo nie, `null` = plné práva.
  const prava = usePrava();
  const jeVyrobnaAktivna = activeVarianta?.je_vyrobna ?? false;
  const rezim = activeVarianta ? rezimVarianty(prava, jeTovar, jeVyrobnaAktivna) : "plny";
  const zamok = activeVarianta ? zamokVarianty(prava, jeTovar, jeVyrobnaAktivna) : null;
  const smieVypocet = prava.nabytok === "vsetko";
  const smieVyrobu = prava.vyroba === "vsetko";
  const novyVypocet = smieVypocet
    ? () => {
        setRezimNovej("vypocet");
        setVariantDialogOpen(true);
      }
    : undefined;
  const novaVyroba = smieVyrobu
    ? () => {
        setRezimNovej(maVyrobnu ? "cast" : "vyrobna");
        setVariantDialogOpen(true);
      }
    : undefined;
  const createVarianta = useCreateVarianta(zakazkaId);
  function novyTovar(): void {
    const obsadene = new Set(varianty.map((v) => v.nazov.toLowerCase()));
    let nazov = "Tovar";
    for (let i = 2; obsadene.has(nazov.toLowerCase()); i += 1) nazov = `Tovar ${i}`;
    createVarianta.mutate(
      { nazov, odbor: "tovar" },
      {
        // Prepne sa, až keď detail zákazky novú variantu načíta (`?varianta=`
        // ako nová časť z importu) — priame prepnutie efekt vyššie hneď vráti
        // na aktívnu, lebo v zozname variant ešte nie je
        onSuccess: (odpoved) =>
          setHladanie(
            (p) => {
              const n = new URLSearchParams(p);
              n.set("varianta", odpoved.data.id);
              return n;
            },
            { replace: true },
          ),
        onError: (err) =>
          toast({
            variant: "destructive",
            title: "Tovar sa nepodarilo založiť",
            description: extractErrorMessage(err),
          }),
      },
    );
  }
  // Dať do ceny zákazky ★ (KOLO 240) — výpočet = aktívny výpočet (smie
  // každý), tovar = aktívny tovar (obchodník); výroba je v cene sama
  const updateZakazka = useUpdateZakazka();
  function datDoCeny(v: Varianta): void {
    const data = jeTovarovaVarianta(v)
      ? { aktivna_tovar_varianta_id: v.id }
      : { aktivna_varianta_id: v.id };
    updateZakazka.mutate(
      { id: zakazkaId, data },
      {
        onSuccess: () =>
          toast({ title: "V cene zákazky", description: `${v.nazov} — cena aj predvolená ponuka` }),
        onError: (err) =>
          toast({
            variant: "destructive",
            title: "Do ceny sa nepodarilo dať",
            description: extractErrorMessage(err),
          }),
      },
    );
  }

  // Výpočet a výroba v jednej zákazke (KOLO 208), spojenie výrob (KOLO 209)
  const strany = stranyPripojenia({
    cislo_zakazky: zakazka.cislo_zakazky,
    vypocet_cislo: zakazka.vypocet_cislo,
    ma_vyrobu: maVyrobnu,
  });

  const activeSloty = useMemo(
    () => (activeVarianta?.sloty ?? []) as MaterialSlot[],
    [activeVarianta],
  );

  // Slot IDs, ktoré používa aspoň jeden dielec — zvyšné sú „nepriradené"
  const pouziteSlotIds = useMemo(() => {
    const ids = new Set<string>();
    for (const z of (activeVarianta?.zostavy ?? []) as Zostava[]) {
      for (const d of (z.dielce ?? []) as Dielec[]) {
        if (d.material_slot_id) ids.add(d.material_slot_id);
      }
    }
    return ids;
  }, [activeVarianta]);

  // Kam ide Ctrl+Enter a Pridať ▾ (dielec, kovanie, komponent, šablóna):
  // zostava vybraného dielca, inak vybraná zostava (klik na pás), až potom
  // prvá. User (KOLO 184): *„keď mám zvolenú zostavu a hore ťuknem pridať
  // dielec, tak ho dá do prvej zostavy, nie do tej zvolenej"* — vybraná
  // zostava sa tu dovtedy nebrala do úvahy.
  const selectedZostavaId = useMemo(() => {
    if (!activeVarianta) return null;
    const zostavy = (activeVarianta.zostavy ?? []) as Zostava[];
    if (selectedDielecId) {
      for (const z of zostavy) {
        if (
          ((z.dielce ?? []) as Dielec[]).some((d) => d.id === selectedDielecId)
        )
          return z.id;
      }
    }
    if (vybranaZostavaId && zostavy.some((z) => z.id === vybranaZostavaId)) {
      return vybranaZostavaId;
    }
    return zostavy[0]?.id ?? null;
  }, [selectedDielecId, vybranaZostavaId, activeVarianta]);
  const cielovaZostavaKod = ((activeVarianta?.zostavy ?? []) as Zostava[]).find(
    (z) => z.id === selectedZostavaId,
  )?.kod;

  /**
   * Na ktoré zostavy ide Pridať dielec / kovanie (KOLO 196): keď je
   * zostava vo viacvýbere, na celý výber v poradí tabuľky, inak len na ňu.
   * `null` = cieľ hornej lišty a klávesov (výber, inak `selectedZostavaId`).
   */
  function cieleZostav(zostavaId: string | null): Zostava[] {
    const zostavy = (activeVarianta?.zostavy ?? []) as Zostava[];
    if (
      vybraneZostavy.size > 0 &&
      (zostavaId === null || vybraneZostavy.has(zostavaId))
    ) {
      return zostavy.filter((z) => vybraneZostavy.has(z.id));
    }
    const z = zostavy.find((zz) => zz.id === (zostavaId ?? selectedZostavaId));
    return z ? [z] : [];
  }
  const cieleListy = cieleZostav(null);
  // Zostavy, ktoré user naozaj vybral (bez náhradnej prvej) — Nový celok
  // ich ponúkne, nová zostava ide do celku vybranej (KOLO 235)
  const vybraneNaozaj: Zostava[] =
    vybraneZostavy.size > 0 || selectedDielecId || vybranaZostavaId ? cieleListy : [];
  const celokVyberu = vybraneNaozaj.length === 1 ? (vybraneNaozaj[0]?.celok_id ?? null) : null;
  const vytvorCelok = useVytvorCelok(zakazkaId, activeVarianta?.id ?? "");
  const cielPopis =
    cieleListy.length > 1
      ? `do ${cieleListy.length} zostáv`
      : cielovaZostavaKod
        ? `do zostavy ${cielovaZostavaKod}`
        : undefined;

  // Selected dielec lookup
  const selectedDielec: Dielec | null = useMemo(() => {
    if (!selectedDielecId || !activeVarianta) return null;
    for (const z of (activeVarianta.zostavy ?? []) as Zostava[]) {
      const found = ((z.dielce ?? []) as Dielec[]).find(
        (d) => d.id === selectedDielecId,
      );
      if (found) return found;
    }
    return null;
  }, [selectedDielecId, activeVarianta]);

  // Lepené vrstvy vybraného dielca (pre split dialóg v detail paneli).
  // Len lep_nc deti — súčasti skla (kalenie…) sa lepených flows netýkajú.
  const selectedVrstvy: Dielec[] = useMemo(() => {
    if (!selectedDielecId || !activeVarianta) return [];
    for (const z of (activeVarianta.zostavy ?? []) as Zostava[]) {
      const vrstvy = ((z.dielce ?? []) as Dielec[]).filter(
        (d) =>
          d.parent_dielec_id === selectedDielecId &&
          d.operacia === OPERACIA_LEPENA_VRSTVA,
      );
      if (vrstvy.length > 0) return vrstvy;
    }
    return [];
  }, [selectedDielecId, activeVarianta]);

  // Súčasti skla vybraného dielca (kalenie, vŕtanie, služby — ne-lep deti)
  const selectedSucasti: Dielec[] = useMemo(() => {
    if (!selectedDielecId || !activeVarianta) return [];
    for (const z of (activeVarianta.zostavy ?? []) as Zostava[]) {
      const deti = ((z.dielce ?? []) as Dielec[]).filter(
        (d) =>
          d.parent_dielec_id === selectedDielecId &&
          d.operacia !== OPERACIA_LEPENA_VRSTVA,
      );
      if (deti.length > 0) return deti;
    }
    return [];
  }, [selectedDielecId, activeVarianta]);

  // Ak sa selectovaný dielec po reloade nenájde, zruš selection
  useEffect(() => {
    if (selectedDielecId && !selectedDielec) {
      setSelectedDielecId(null);
    }
  }, [selectedDielec, selectedDielecId]);

  // Dielce vo viacnásobnom výbere — pre hromadnú úpravu. Filtrujeme na tie,
  // ktoré v tejto variante reálne existujú: po zmazaní alebo prepnutí
  // výpočtu by inak vo výbere zostali mŕtve id.
  const vybraneObjekty: Dielec[] = useMemo(() => {
    if (vybraneDielce.size === 0 || !activeVarianta) return [];
    const out: Dielec[] = [];
    for (const z of (activeVarianta.zostavy ?? []) as Zostava[]) {
      for (const d of (z.dielce ?? []) as Dielec[]) {
        if (vybraneDielce.has(d.id)) out.push(d);
      }
    }
    return out;
  }, [vybraneDielce, activeVarianta]);

  const jeHromadny = vybraneObjekty.length > 1;

  /**
   * Označiť v tabuľke (KOLO 233) — user: *„k materiálu, hrane alebo
   * kovaniu zobraziť / označiť, kde sa v akých riadkoch nachádzajú"*.
   * Jeden riadok sa vyberie ako klik (detail), viac ako Ctrl+klik
   * (hromadná úprava); tabuľka ich rozbalí a posunie sa na prvý.
   */
  function oznacVTabulke(ciel: CielOznacenia, nazov: string): void {
    if (!activeVarianta) return;
    const o = riadkyNaOznacenie(activeVarianta, ciel);
    if (o.dielce.length === 0) {
      toast({ title: "V tabuľke nie je", description: `${nazov} nemá žiadny riadok.` });
      return;
    }
    setVybranaZostavaId(null);
    setVybraneZostavy(new Set());
    if (o.dielce.length === 1) {
      setVybraneDielce(new Set());
      setSelectedDielecId(o.dielce[0] ?? null);
    } else {
      setSelectedDielecId(null);
      setVybraneDielce(new Set(o.dielce));
    }
    setOdhalit({ ...o, token: Date.now() });
    toast({
      title: `Označené: ${o.dielce.length} ${sklonuj(o.dielce.length, ["riadok", "riadky", "riadkov"])} v ${o.pocetZostav} ${sklonuj(o.pocetZostav, ["zostave", "zostavách", "zostavách"])}`,
      description: nazov,
    });
  }

  // Vybraná zostava (klik na riadok zostavy) — pre detail s maržou
  const vybranaZostava: Zostava | null = useMemo(() => {
    if (!vybranaZostavaId || !activeVarianta) return null;
    return (
      ((activeVarianta.zostavy ?? []) as Zostava[]).find(
        (z) => z.id === vybranaZostavaId,
      ) ?? null
    );
  }, [vybranaZostavaId, activeVarianta]);

  useEffect(() => {
    if (vybranaZostavaId && !vybranaZostava) {
      setVybranaZostavaId(null);
    }
  }, [vybranaZostava, vybranaZostavaId]);

  /**
   * Mini-lišta hrán má zmysel len vtedy, keď pás **nie je** na očiach a
   * editovať je čo. Pri hromadnom výbere nie — tam je v páse
   * `HromadnaUpravaPanel`, ktorý hrany rieši pre všetky vybrané naraz.
   * Pri pripnutom páse nie — ten cestuje celý, lišta by ho duplikovala.
   */
  const ukazListu =
    detailViditelny &&
    !pasPripnuty &&
    !pasVidno &&
    !jeHromadny &&
    selectedDielec != null;

  // ── klávesové skratky editora ──
  const [skratkyOpen, setSkratkyOpen] = useState(false);
  const [porovnanieOpen, setPorovnanieOpen] = useState(false);
  const [cenaHistoriaOpen, setCenaHistoriaOpen] = useState(false);
  const [opravyOpen, setOpravyOpen] = useState(false);
  const hkDeleteDielec = useDeleteDielec(zakazkaId);
  const hkDeleteZostava = useDeleteZostava(zakazkaId);
  const hkDuplicateZostava = useDuplicateZostava(zakazkaId);
  const hkPoradie = usePoradieDielcov(zakazkaId);
  const hkPoradieZostav = usePoradieZostav(zakazkaId);

  // Flat zoznam top-level dielcov (poradie tabuľky) pre šípky ↑/↓
  const dielceFlatIds = useMemo(() => {
    const ids: string[] = [];
    for (const z of (activeVarianta?.zostavy ?? []) as Zostava[]) {
      for (const d of (z.dielce ?? []) as Dielec[]) {
        if (!d.parent_dielec_id) ids.push(d.id);
      }
    }
    return ids;
  }, [activeVarianta]);

  // Meranie času na zákazke — pulz, kým je editor otvorený a človek pri
  // ňom. Nič neinvaliduje, takže do prepočtov cien nezasahuje.
  useHeartbeat(zakazkaId);

  const undo = useUndo();
  // Sprievodca importu žije v `AppShell` (KOLO 117); odtiaľto sa len
  // otvára — v režime update na aktívny výpočet (KOLO 118).
  const { otvorUpdate } = useImport();

  async function vratZmenu(): Promise<void> {
    if (undo.bezi) return;
    try {
      const popis = await undo.vrat();
      toast(
        popis ? { title: `Vrátené: ${popis}` } : { title: "Niet čo vrátiť" },
      );
    } catch (e) {
      // Krok už zo zásobníka vypadol — Ctrl+Z nemá narážať do tej istej
      // steny dookola (typicky 409, keď medzitým zmizol rodič).
      toast({
        variant: "destructive",
        title: "Vrátiť sa nepodarilo",
        description: extractErrorMessage(e),
      });
    }
  }

  // Schránka riadkov (KOLO 181) — Ctrl+C/X/V robí to isté ako menu riadku
  const schrankaRiadkov = useAkcieSchranky({
    zakazkaId,
    zakazkaCislo: zakazka.cislo_zakazky ?? null,
    variantaId: activeVarianta?.id ?? "",
  });
  const dielceVarianty = ((activeVarianta?.zostavy ?? []) as Zostava[]).flatMap(
    (z) => (z.dielce ?? []) as Dielec[],
  );

  /** Ctrl+C / Ctrl+X: viacvýber v poradí tabuľky, inak vybraný riadok. */
  function riadkyDoSchranky(): Dielec[] {
    const kusovnik = dielceVarianty.filter(
      (d) => !d.parent_dielec_id && !jeOperaciaRiadok(d),
    );
    if (vybraneDielce.size > 0)
      return kusovnik.filter((d) => vybraneDielce.has(d.id));
    return kusovnik.filter((d) => d.id === selectedDielecId);
  }

  const skratky: EditorHotkeys = {
    enabled: Boolean(activeVarianta),
    onUndo: () => void vratZmenu(),
    onKopirovat: () => schrankaRiadkov.kopiruj(riadkyDoSchranky()),
    onVystrihnut: () => schrankaRiadkov.vystrihni(riadkyDoSchranky()),
    onVlozit: () => {
      // Pod vybraný riadok (pod vrstvou/operáciou = pod jej dielec),
      // paušál a vybraná zostava = na koniec zostavy
      const d = dielceVarianty.find((x) => x.id === selectedDielecId);
      if (d) {
        const za = d.parent_dielec_id ?? (jeOperaciaRiadok(d) ? null : d.id);
        void schrankaRiadkov.vloz(d.zostava_id, za);
      } else if (vybranaZostavaId) {
        void schrankaRiadkov.vloz(vybranaZostavaId, null);
      }
    },
    onAddDielec: () => {
      const [prva] = cieleZostav(null);
      if (prva) void pridajDielec(prva.id);
    },
    onAddZostava: () => {
      setZostavaPo(null);
      setZostavaDoCelku(celokVyberu);
      setZostavaDialogOpen(true);
    },
    onZrusVyber: () => {
      setVybraneDielce(new Set());
      setVybraneZostavy(new Set());
      schrankaRiadkov.zrusVystrihnutie();
    },
    onDuplicate: () => {
      // Viacvýber zostáv (KOLO 196) — kópia každej, jeden krok späť
      if (vybraneZostavy.size > 1) {
        const ciele = cieleZostav(null);
        void hromadne
          .duplikuj(ciele, (activeVarianta?.zostavy ?? []) as Zostava[])
          .then((v) => {
            setVybraneZostavy(new Set());
            toast(
              v.chyby.length === 0
                ? {
                    title: `Zduplikované: ${ciele.length} ${sklonuj(ciele.length, ["zostava", "zostavy", "zostáv"])}`,
                  }
                : {
                    variant: "destructive",
                    title: `Zduplikované ${v.hotovo.length}, ${v.chyby.length} neprešlo`,
                    description: v.chyby
                      .map((c) => `${c.kod}: ${c.sprava}`)
                      .join(" · "),
                  },
            );
          });
        return;
      }
      const cielId = selectedZostavaId;
      const zostavy = (activeVarianta?.zostavy ?? []) as Zostava[];
      const z = zostavy.find((zz) => zz.id === cielId);
      if (!z || hkDuplicateZostava.isPending) return;
      // Duplikát ide hneď za zdroj — backend ho tam zaradí a prečísluje
      const newKod = pismena(zostavy.findIndex((zz) => zz.id === z.id) + 1);
      hkDuplicateZostava.mutate(
        {
          id: z.id,
          data: { new_kod: newKod, new_nazov: `${z.nazov} (kópia)` },
        },
        {
          onSuccess: () =>
            toast({ title: "Zostava zduplikovaná", description: newKod }),
          onError: (err) =>
            toast({
              variant: "destructive",
              title: "Chyba pri duplikovaní",
              description: extractErrorMessage(err),
            }),
        },
      );
    },
    onDelete: () => {
      if (vybraneZostavy.size > 1) {
        const ciele = cieleZostav(null);
        const n = `${ciele.length} ${sklonuj(ciele.length, ["zostava", "zostavy", "zostáv"])}`;
        if (
          !window.confirm(
            `Naozaj zmazať ${n} (${ciele.map((z) => z.kod).join(", ")})?`,
          )
        )
          return;
        void hromadne.zmaz(ciele).then((v) => {
          setVybraneZostavy(new Set());
          setVybranaZostavaId(null);
          toast(
            v.chyby.length === 0
              ? { title: `Zmazané: ${n}` }
              : {
                  variant: "destructive",
                  title: `Zmazané ${v.hotovo.length}, ${v.chyby.length} neprešlo`,
                  description: v.chyby
                    .map((c) => `${c.kod}: ${c.sprava}`)
                    .join(" · "),
                },
          );
        });
        return;
      }
      if (selectedDielecId) {
        if (!window.confirm("Zmazať vybraný dielec?")) return;
        hkDeleteDielec.mutate(selectedDielecId, {
          onSuccess: () => {
            setSelectedDielecId(null);
            toast({ title: "Dielec zmazaný" });
          },
        });
        return;
      }
      if (vybranaZostavaId) {
        const z = ((activeVarianta?.zostavy ?? []) as Zostava[]).find(
          (zz) => zz.id === vybranaZostavaId,
        );
        if (!z) return;
        if (!window.confirm(`Naozaj zmazať zostavu "${z.kod}"?`)) return;
        hkDeleteZostava.mutate(z.id, {
          onSuccess: () => {
            setVybranaZostavaId(null);
            toast({ title: "Zostava zmazaná" });
          },
        });
      }
    },
    onMoveSelection: (smer) => {
      if (dielceFlatIds.length === 0) return;
      const idx = selectedDielecId
        ? dielceFlatIds.indexOf(selectedDielecId)
        : -1;
      const next =
        idx === -1
          ? smer === 1
            ? 0
            : dielceFlatIds.length - 1
          : Math.min(Math.max(idx + smer, 0), dielceFlatIds.length - 1);
      setSelectedDielecId(dielceFlatIds[next] ?? null);
      setVybranaZostavaId(null);
      setVybraneZostavy(new Set());
    },
    onPosunRiadok: (smer) => {
      // Alt+↑/↓ na vybranej zostave (klik na pás) posunie celú zostavu
      // (KOLO 183)
      if (!selectedDielecId && vybranaZostavaId && activeVarianta) {
        if (hkPoradieZostav.isPending) return;
        const zostavyV = (activeVarianta.zostavy ?? []) as Zostava[];
        // Len v celku (KOLO 235) — do iného sa ťahá alebo Dať do celku
        if (!susedVSkupine(zostavyV, vybranaZostavaId, smer)) return;
        const ids = zostavyV.map((z) => z.id);
        const nove = posunOJeden(ids, new Set(ids), vybranaZostavaId, smer);
        if (rovnakePoradie(nove, ids)) return;
        const kod =
          ((activeVarianta.zostavy ?? []) as Zostava[]).find(
            (z) => z.id === vybranaZostavaId,
          )?.kod ?? "";
        hkPoradieZostav.mutate(
          {
            variantaId: activeVarianta.id,
            zostavy: nove,
            popis: `posunutie zostavy ${kod}`,
          },
          {
            onError: (err) =>
              toast({
                variant: "destructive",
                title: "Zostavu sa nepodarilo posunúť",
                description: extractErrorMessage(err),
              }),
          },
        );
        return;
      }
      // Alt+↑/↓ posunie vybraný riadok v jeho zostave (KOLO 176)
      if (!selectedDielecId || hkPoradie.isPending) return;
      const z = ((activeVarianta?.zostavy ?? []) as Zostava[]).find((zz) =>
        (zz.dielce ?? []).some((d) => d.id === selectedDielecId),
      );
      if (!z) return;
      const { korene, posuvatelne } = poradieZostavy(
        (z.dielce ?? []) as Dielec[],
      );
      const nove = posunOJeden(korene, posuvatelne, selectedDielecId, smer);
      if (rovnakePoradie(nove, korene)) return;
      const kod =
        (z.dielce ?? []).find((d) => d.id === selectedDielecId)?.kod ?? "";
      hkPoradie.mutate(
        { zostavaId: z.id, dielce: nove, popis: `posunutie riadku ${kod}` },
        {
          onError: (err) =>
            toast({
              variant: "destructive",
              title: "Riadok sa nepodarilo posunúť",
              description: extractErrorMessage(err),
            }),
        },
      );
    },
    onHelp: () => setSkratkyOpen(true),
  };
  // Skratky podľa režimu (KOLO 231). V tovare len tie, čo nič nemenia —
  // chyba z KOLA 230: Ctrl+Enter pridal do regálu dielec ako v nábytku
  // a Ctrl+D / Delete mierili na výber z tabuľky nábytku, ktorá v tovare
  // nie je. Pri zámku (obchodník v nábytku) len pohyb, kopírovanie a späť.
  useEditorHotkeys(
    jeTovar
      ? { enabled: skratky.enabled, onUndo: skratky.onUndo, onHelp: skratky.onHelp }
      : zamok
        ? {
            enabled: skratky.enabled,
            onUndo: skratky.onUndo,
            onHelp: skratky.onHelp,
            onKopirovat: skratky.onKopirovat,
            onZrusVyber: skratky.onZrusVyber,
            onMoveSelection: skratky.onMoveSelection,
          }
        : skratky,
  );

  // Quick-add: dielec vznikne rovno v tabuľke (bez dialógu) s kódom
  // {zostavaKod}_p{n} a hneď sa otvorí editácia názvu.
  const quickCreateMutation = useQuickCreateDielec(zakazkaId);

  const hromadne = useHromadneZostavy(zakazkaId);

  /** Nový prázdny dielec do zostavy — to isté pre jednu aj viac zostáv. */
  function novyDielec(z: Zostava): DielecCreate {
    const dielce = (z.dielce ?? []) as Dielec[];
    // nový dielec dostane posledný slot, ktorý používateľ vyberal
    const lastSlot = activeVarianta ? getLastSlot(activeVarianta.id) : null;
    const slot = (activeVarianta?.sloty ?? []).find((s) => s.id === lastSlot);
    // Smer vlákien len pri materiáli s drevnou štruktúrou
    const maStrukturu =
      !slot?.material || slot.material.smer_vlakien_default !== "0";
    return {
      kod: suggestNextKod(z.kod, dielce),
      material_slot_id: slot ? slot.id : null,
      pocet_ks: 1,
      nadmiera_x_mm: "5.00",
      nadmiera_y_mm: "5.00",
      nadmiera_aktivna: true,
      smer_vlakien: maStrukturu ? "X" : "0",
      // Bez hrán, kým názov s predvoľbou hrany nezapne (KOLO 159 —
      // user: „jedine kedy budú hrany zapnuté, je keď názov dielca ich
      // má prednastavené"); hrúbku dá backend podľa materiálu slotu
      hrana_x1_auto: false,
      hrana_x2_auto: false,
      hrana_y1_auto: false,
      hrana_y2_auto: false,
      poradie: dielce.length,
    };
  }

  /**
   * Pridať dielec — do zostavy, pri viacvýbere zostáv do každej (KOLO 196)
   * jedným krokom späť; vtedy sa žiadny riadok neotvára na písanie.
   */
  async function pridajDielec(zostavaId: string): Promise<void> {
    const ciele = cieleZostav(zostavaId);
    if (ciele.length <= 1) {
      await quickAddDielec(zostavaId);
      return;
    }
    const v = await hromadne.pridajDielce(
      ciele.map((z) => ({ zostavaId: z.id, kod: z.kod, data: novyDielec(z) })),
      `pridanie dielca do ${ciele.length} zostáv`,
    );
    toast(
      v.chyby.length === 0
        ? {
            title: `Dielec pridaný do ${ciele.length} zostáv`,
            description: ciele.map((z) => z.kod).join(", "),
          }
        : {
            variant: "destructive",
            title: `Dielec pridaný do ${v.hotovo.length}, ${v.chyby.length} neprešlo`,
            description: v.chyby
              .map((c) => `${c.kod}: ${c.sprava}`)
              .join(" · "),
          },
    );
  }

  /**
   * Pridať kovanie / cenovku — jedno okno, pri viacvýbere zostáv do každej
   * (KOLO 196, cenovka KOLO 197).
   */
  function otvorPridanie(
    zostavaId: string,
    rezim: "kovanie" | "cenovka" | "vlastna",
  ): void {
    const [prva, ...dalsie] = cieleZostav(zostavaId);
    if (!prva) return;
    setDielecCreateContext({
      zostavaId: prva.id,
      zostavaKod: prva.kod,
      rezim,
      dalsie: dalsie.map((z) => z.id),
    });
  }
  const pridajKovanie = (zostavaId: string): void =>
    otvorPridanie(zostavaId, "kovanie");
  const pridajCenovku = (zostavaId: string): void =>
    otvorPridanie(zostavaId, "cenovka");
  const pridajVlastnu = (zostavaId: string): void =>
    otvorPridanie(zostavaId, "vlastna");

  /** Ďalšie vybrané zostavy pre okno pridania — s ich riadkami (kód, poradie). */
  function dalsieZostavyPridania(
    ids: string[] | undefined,
  ): { id: string; kod: string; existingDielce: Dielec[] }[] {
    const zostavy = (activeVarianta?.zostavy ?? []) as Zostava[];
    return (ids ?? []).flatMap((id) => {
      const z = zostavy.find((zz) => zz.id === id);
      return z ? [{ id: z.id, kod: z.kod, existingDielce: (z.dielce ?? []) as Dielec[] }] : [];
    });
  }

  async function quickAddDielec(zostavaId: string): Promise<void> {
    const z = ((activeVarianta?.zostavy ?? []) as Zostava[]).find(
      (zz) => zz.id === zostavaId,
    );
    if (!z) return;
    try {
      const created = await quickCreateMutation.mutateAsync({
        zostavaId,
        data: novyDielec(z),
      });
      setSelectedDielecId(created.data.id);
      setFocusDielecId(created.data.id);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Dielec sa nepodarilo pridať",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    // Editor berie celú zvyšnú výšku okna a sám nescrolluje — scrolluje
    // výhradne telo tabuľky dielcov (KOLO 56).
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <ZakazkaHeader
        zakazka={zakazka}
        akcie={
          <AkcieZakazkyMenu
            zakazkaId={zakazkaId}
            pocetVypoctov={variantyOdboru(varianty, "nabytok").length}
            maVyrobnu={maVyrobnu}
            onPorovnat={() => setPorovnanieOpen(true)}
            onCenaHistoria={() => setCenaHistoriaOpen(true)}
            pripojit={(smieVyrobu ? strany : []).map((strana) => ({
              label: NAZOV_PRIPOJENIA[strana],
              title: POPIS_PRIPOJENIA[strana],
              onSelect: () => setPripojitStrana(strana),
            }))}
            onImportTopSolid={
              prava.nastavenia_vyroby ? () => setTopsolidDialogOpen(true) : undefined
            }
            onOpravy={() => setOpravyOpen(true)}
            onSkratky={() => setSkratkyOpen(true)}
          />
        }
      />
      {/* Lišta odborov (KOLO 240, návrh H1b) — Nábytok · Tovar · Výroba
          s cenou a ponukou s podmienkami (do KOLA 239 pás Rozpočet → Ponuka) */}
      <ListaOdborov
        zakazka={zakazka}
        odbor={odbor}
        onOdbor={prepniOdbor}
        novy={{
          nabytok: novyVypocet,
          tovar: prava.tovar === "vsetko" ? novyTovar : undefined,
          vyroba: novaVyroba,
        }}
        rezimy={{
          nabytok: rezimVarianty(prava, false, false),
          tovar: rezimVarianty(prava, true),
          vyroba: rezimVarianty(prava, false, true),
        }}
      />
      {/* Diely Tandemu z regálu čakajú na rozpočtára (KOLO 232) */}
      <PasRegalNacenit zakazkaId={zakazka.id} />

      {/* Toolbar v troch zónach podľa účelu (KOLO 57):
            kontext (na čom pracujem) │ pridávanie │ zobrazenie a výstup.
          `ml-auto` na tretej zóne namiesto `justify-between` — to by zóny
          1 a 2 od seba odtrhlo podľa šírky okna a oddeľovač by sa ocitol
          raz tu, raz tam. `flex-wrap` je zrušený: po zúžení na ~870 px sa
          toolbar nemá prečo zalamovať a wrap by tichým skokom na 92 px
          zase posunul tabuľku nižšie.
          Pod `md` sa zalamuje (KOLO 212): na mobile sa záložky a tlačidlá
          do jedného riadku nezmestia — bez zalomenia sa prekrývali a Výstupy
          boli na 493–607 px pri šírke 390, dosiahnuteľné len posunutím celej
          stránky do strany. Záložky majú vlastný riadok a posúvajú sa. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 md:flex-nowrap md:gap-3">
        <div className="flex w-full min-w-0 items-center gap-1 md:w-auto">
          {/* Verzie vybraného odboru (KOLO 240) — ★ = v cene zákazky, ⋮ pri
              vybranej; do KOLA 239 záložky všetkých variant a jedno ⋮ */}
          {odbor && activeVarianta ? (
            <KapsuleVerzii
              zakazka={zakazka}
              odbor={odbor}
              verzie={verzieOdboru}
              aktivnaId={activeVariantaId}
              onVyber={setActiveVariantaId}
              onNova={
                odbor === "nabytok"
                  ? novyVypocet
                  : odbor === "tovar"
                    ? prava.tovar === "vsetko"
                      ? novyTovar
                      : undefined
                    : novaVyroba
              }
              menu={
                <AkcieVerzieMenu
                  nazov={activeVarianta.nazov}
                  druh={
                    jeTovar
                      ? "tovar"
                      : activeVarianta.je_cast
                        ? "cast"
                        : activeVarianta.je_vyrobna
                          ? "vyroba"
                          : "vypocet"
                  }
                  onPremenovat={zamok ? undefined : () => setPremenovat(activeVarianta)}
                  vCene={jeVCene(zakazka, activeVarianta)}
                  onDatDoCeny={
                    activeVarianta.je_vyrobna || (jeTovar && prava.tovar !== "vsetko")
                      ? undefined
                      : () => datDoCeny(activeVarianta)
                  }
                  onUpdateZExportu={
                    prava.nastavenia_vyroby && !jeTovar
                      ? () => otvorUpdate(zakazkaId, activeVarianta.id)
                      : undefined
                  }
                  maPovodDielcov={activeVarianta.ma_povod_z_topsolidu ?? false}
                  onSlotyZMaterialov={
                    zamok || jeTovar ? undefined : spustiSlotyZMaterialov
                  }
                  slotyBezia={slotyMutation.isPending}
                  onZmazat={zamok ? undefined : () => void handleDeleteVarianta()}
                  mazanieBezi={deleteVarianta.isPending}
                />
              }
            />
          ) : null}
        </div>

        <div className="hidden h-6 w-px shrink-0 bg-border md:block" />

        <div className="flex shrink-0 items-center gap-2">
          {/* Bez práva na štruktúru (KOLO 231) pridávanie nie je — obchodník
              v nábytku mení cenu v paneli zostavy, rozpočtár tovar číta */}
          {zamok ? null : (
          <Button
            size="sm"
            onClick={() => {
              setZostavaPo(null);
              setZostavaDoCelku(celokVyberu);
              setZostavaDialogOpen(true);
            }}
            disabled={!activeVarianta}
            title="Pridať novú zostavu (produkt) do výpočtu (Ctrl+Shift+Z)"
          >
            <Plus className="h-4 w-4" />
            Zostava
          </Button>
          )}
          {/* „+ Dielec" odišiel do Pridať ▾ (KOLO 196) — user: „hore daj len
              pridať zostavu, dielec viem pridávať aj inak". V menu ostal
              prvý so skratkou a cieľom: pravé tlačidlo a Ctrl+Enter sám
              nikto nenájde. „Dielec cez formulár" bol zrušený v KOLE 55. */}
          {jeTovar ? (
            <TlacidlaTovaru
              varianta={activeVarianta}
              lenCitanie={zamok != null}
              zostavaId={zostavaTovaruId}
              onTovar={setPridatTovarDo}
              onVlastna={pridajVlastnu}
              onRegal={() => setRegalTovaru("")}
            />
          ) : null}
          {jeTovar || zamok ? null : (
          <PridatMenu
            maVypocet={!!activeVarianta}
            maVybranuZostavu={
              cieleListy.length > 0 && !quickCreateMutation.isPending
            }
            cielPopis={cielPopis}
            onDielec={() => {
              const [prva] = cieleListy;
              if (prva) void pridajDielec(prva.id);
            }}
            onKovanie={() => {
              const [prva] = cieleListy;
              if (prva) pridajKovanie(prva.id);
            }}
            onCenovka={() => {
              const [prva] = cieleListy;
              if (prva) pridajCenovku(prva.id);
            }}
            onVlastna={() => {
              const [prva] = cieleListy;
              if (prva) pridajVlastnu(prva.id);
            }}
            onKomponent={() => setKomponentDialogOpen(true)}
            onSablona={() => setSablonaInsertOpen(true)}
            onCelok={activeVarianta ? () => setNovyCelokOpen(true) : undefined}
          />
          )}
        </div>

        {/* Zóna 3 — zobrazenie a výstup. Nič nepridáva, preto je oddelená. */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <AutoSaveIndicator />
          {/* Skratka Ctrl+Z je neviditeľná — bez tlačidla by nikto
              nevedel, že sa dá vrátiť (lekcia z checkboxov v KOLE 58). */}
          <Button
            size="sm"
            variant="outline"
            disabled={undo.pocet === 0 || undo.bezi}
            onClick={() => void vratZmenu()}
            title={
              undo.posledny
                ? `Vrátiť ${undo.posledny} (Ctrl+Z)`
                : "Niet čo vrátiť — zásobník vrátení je prázdny"
            }
          >
            <Undo2 className="h-4 w-4" />
            Vrátiť
          </Button>
          {jeTovar ? null : (
          <Button
            size="icon"
            variant={detailViditelny ? "secondary" : "outline"}
            className="h-9 w-9"
            onClick={() => setDetailViditelny((v) => !v)}
            aria-label={
              detailViditelny ? "Skryť detail dielca" : "Zobraziť detail dielca"
            }
            title={
              detailViditelny
                ? "Skryť detail dielca — tabuľka dostane zhruba dvojnásobok riadkov"
                : "Zobraziť pás detailu dielca (hrany, materiál, prírez)"
            }
            aria-pressed={detailViditelny}
          >
            {detailViditelny ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </Button>
          )}
          {/* Pripnutie sedí hneď pri „skryť/zobraziť" — obe sú o tom, ako sa
              pás zobrazuje. Pri skrytom páse tlačidlo nie je: pripínať to,
              čo nie je vidno, nemá zmysel a toolbar by len rástol. */}
          {detailViditelny && !jeTovar ? (
            <Button
              size="icon"
              variant={pasPripnuty ? "secondary" : "outline"}
              className="h-9 w-9"
              onClick={() => setPasPripnuty((v) => !v)}
              aria-label={
                pasPripnuty ? "Odopnúť detail dielca" : "Pripnúť detail dielca"
              }
              title={
                pasPripnuty
                  ? "Celý detail je pripnutý — pri scrollovaní zostáva na mieste. Klik: odopnúť a namiesto neho ukazovať úzku lištu hrán."
                  : "Detail odíde s obsahom a keď zmizne z dohľadu, hore sa pripne úzka lišta hrán. Klik: pripnúť celý detail."
              }
              aria-pressed={pasPripnuty}
            >
              {pasPripnuty ? (
                <Pin className="h-4 w-4" />
              ) : (
                <PinOff className="h-4 w-4" />
              )}
            </Button>
          ) : null}
          {activeVarianta ? (
            <VystupyMenu zakazka={zakazka} varianta={activeVarianta} />
          ) : null}
        </div>
      </div>

      {activeVarianta ? (
        <SablonaInsertDialog
          zakazkaId={zakazkaId}
          variantaId={activeVarianta.id}
          sloty={(activeVarianta.sloty ?? []) as MaterialSlot[]}
          open={sablonaInsertOpen}
          onOpenChange={setSablonaInsertOpen}
        />
      ) : null}
      <SkratkyDialog open={skratkyOpen} onOpenChange={setSkratkyOpen} />
      <PorovnanieDialog
        open={porovnanieOpen}
        onOpenChange={setPorovnanieOpen}
        varianty={varianty}
        aktivnaVariantaId={zakazka.aktivna_varianta_id}
      />
      {/* Až po kliknutí — dotaz na históriu nemá bežať pri každom otvorení
          editora, snímky sa menia len pri prechode stavu. */}
      {cenaHistoriaOpen ? (
        <CenaHistoriaDialog
          open
          onOpenChange={setCenaHistoriaOpen}
          zakazkaId={zakazka.id}
        />
      ) : null}

      {opravyOpen ? (
        <OpravyDialog
          open
          onOpenChange={setOpravyOpen}
          zakazkaId={zakazka.id}
        />
      ) : null}

      {activeVarianta && jeTovar ? (
        /* Rozpočet tovaru (KOLO 230) — regály CRC bez hrán, rozmerov
           a výroby; vlastný pohľad namiesto tabuľky dielcov a pásu detailu */
        <TovarVarianta
          zakazka={zakazka}
          varianta={activeVarianta}
          lenCitanie={zamok != null}
          onNovaZostava={() => {
            setZostavaPo(null);
            setZostavaDialogOpen(true);
          }}
          onVlastna={pridajVlastnu}
          regal={regalTovaru}
          onRegal={setRegalTovaru}
          pridatDo={pridatTovarDo}
          onPridatDo={setPridatTovarDo}
          onVyberZostavu={setZostavaTovaruId}
        />
      ) : activeVarianta ? (
        /* Pás detailu je od KOLA 67 späť **v ľavom stĺpci mriežky**, nie nad
           ňou cez plnú šírku (KOLO 63 → 64). Dôvod: cez plnú šírku spadol
           pravý panel pod pás a pri viewporte 630 px mu zostalo ~160 px,
           takže tab Rezanie (~400 px) scrolloval. V stĺpci je riadok mriežky
           vysoký ako ľavý stĺpec (325 + 12 + 160 ≈ 497 px) a **pravý panel
           začína hore** s celou touto výškou.

           Predpoklad, na ktorom to stojí: hlavička detailu sa v stĺpci
           (864 px) **nesmie zalamovať**. Preto z nej v KOLE 67 odišli
           Triedenie, checkbox Nadmiery a Rotácia — bez nich potrebuje 703 px.
           Bez tejto úpravy by to bol krok späť do stavu pred KOLOM 63.

           Toto NIE JE `<main>` — ten je v AppShelli; dva landmarky boli
           chyba, ktorú presun pásu odhalil. */
        <>
          {/* Výšku pásu vlastní TENTO obal (KOLO 63), vetvy majú `h-full`.
              Predtým si ju definovala každá vetva sama — vetvy s hard
              `h-[313px]` mali 313, vetvy „hlavička + lg:h-[260px]" 315
              (bordery karty), takže tabuľka pri prekliku poskočila o 2 px.
              A pod `lg` vetvy bez hard výšky nemali strop vôbec.

              Pás je prvý prvok ĽAVÉHO stĺpca a pri pripnutí `sticky top-0`
              (KOLO 64 ho dalo hore, KOLO 67 do stĺpca). Na nízkom okne `main`
              z AppShellu scrolluje tak či tak: po 201 px chrome a 325 px páse
              zostane pri viewporte 630 px (notebook 1366×768) na mriežku
              ~104 px a `min-h-[160px]` tabuľky to dotlačí do scrollu. Keď sa
              layout zmestí, sticky je no-op.

              `top-0` nepotrebuje offset: scrollport je `main`, ktorý začína
              pod `Header`-om, takže sa pás pripne presne pod neho. Hlavička
              zákazky a toolbar nad ním pri scrollovaní odídu — sú to veci,
              ktoré sa počas práce s hranami nepotrebujú.

              Toto je reverz KOLA 56, ktoré sticky v editore zrušilo — tam
              však boli **počítané offsety na dvoch elementoch**, tu je jeden
              element bez offsetu.

              `h-[325px] pb-3 bg-background`: karta má 313 px a posledných
              12 px obalu je **nepriehľadný odstup** pred tabuľkou. Ľavý stĺpec
              `gap` nemá (na rozdiel od mriežky pred KOLOM 67), takže `-mb-3`
              je zrušené — nie je čo získavať a tabuľka by sa pod pás natrvalo
              podsunula. Pri `h-[313px]` by `pb-3` kartu stlačilo na 301 a celý
              invariant by tichým spôsobom padol.

              Pripnutie je od KOLA 66 **voľba** (`tandem_pas_pripnuty`), ale
              podmienené je len `sticky`, `z-20` a tieň. **Výška, `-mb-3 pb-3`
              a `bg-background` zostávajú vždy** — inak by existovali dva
              výškové režimy a tabuľka by pri odopnutí poskočila. Tieň dáva
              zmysel len pripnutému (oddeľuje pás od obsahu, ktorý sa pod
              neho podsúva); odopnutému sa nič nepodsúva. */}
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
            {/* `min-w-0` je rovnako povinné ako `min-h-0`: grid track
                `1fr` má implicitné `min-width: auto`, takže sa nezmrští
                pod min-content šírku tabuľky. Dlhý kód zostavy
                z TopSolidu (`TAN10989_pult_zasuvka_sokel_koncovy_L`) tak
                roztlačil celý editor a **pravý panel odtlačil mimo
                obrazovku** — stránka začala scrollovať vodorovne, čo je
                presne to, čo KOLO 56 zakázalo. */}
            <div className="flex min-h-0 min-w-0 flex-col">
              {detailViditelny ? (
                <div
                  ref={pasRef}
                  className={cn(
                    "h-[325px] shrink-0 overflow-hidden bg-background pb-3",
                    pasPripnuty &&
                      "sticky top-0 z-20 shadow-[0_2px_6px_rgba(0,0,0,0.06)]",
                    // Na mobile (KOLO 212) prázdny pás „Klikni na dielec…"
                    // nie je — zaberal pol obrazovky nad tabuľkou. Výškový
                    // invariant vyššie platí pre počítač, kde tabuľka pod
                    // pásom nesmie poskočiť.
                    !jeHromadny && !vybranaZostava && !selectedDielec &&
                      "max-lg:hidden",
                  )}
                  data-testid="detail-panel-wrap"
                >
                  {jeHromadny && !zamok ? (
                    /* Viac vybraných dielcov → panel sa prepne na hromadnú
                       úpravu. Výšku berie z obalu, takže tabuľka neposkočí. */
                    <HromadnaUpravaPanel
                      zakazkaId={zakazkaId}
                      dielce={vybraneObjekty}
                      sloty={activeSloty}
                      onHotovo={() => setVybraneDielce(new Set())}
                    />
                  ) : vybranaZostava ? (
                    <ZostavaDetailPanel
                      zakazkaId={zakazkaId}
                      zostava={vybranaZostava}
                      defaultZiskPct={activeVarianta.zisk_pct}
                      variantaId={activeVarianta.id}
                      sloty={activeSloty}
                      rezim={rezim}
                      dovodZamku={zamok ?? undefined}
                    />
                  ) : (
                    <DielecDetailPanel
                      zakazkaId={zakazkaId}
                      dielec={selectedDielec}
                      sloty={activeSloty}
                      vrstvy={selectedVrstvy}
                      sucasti={selectedSucasti}
                      onEditSklo={() => setSkloEditTarget(selectedDielec)}
                      zamok={zamok}
                    />
                  )}
                </div>
              ) : null}
              {/* Mini-lišta hrán (KOLO 67) — 48 px, ktoré cestujú so
                scrollom namiesto 325 px pásu. Zobrazí sa LEN keď je pás
                odopnutý a odscrollovaný z dohľadu: vtedy by klik na dielec
                skončil pri editore hrán, ktorý nie je vidno. Nahrádza
                `scrollIntoView` z KOLA 66 — netreba nikam skákať, editor
                príde za tebou a riadok, na ktorý si klikol, zostane na
                mieste. */}
              {ukazListu && selectedDielec ? (
                /* `h-0` + absolútny obsah: lišta OVERLAYUJE horné pixely
                 tabuľky namiesto toho, aby ju pri objavení posunula o 48 px
                 nižšie. Sticky obálka s nulovou výškou sa pripne presne vo
                 chvíli, keď spodok pásu prejde hornou hranou scrollportu. */
                <div className="sticky top-0 z-30 h-0">
                  <div className="absolute inset-x-0 top-0">
                    <HranyLista zakazkaId={zakazkaId} dielec={selectedDielec} />
                  </div>
                </div>
              ) : null}
              {/* Jediná scrollovacia oblasť editora. `min-h-[160px]` bráni
                tomu, aby sa tabuľka na nízkom okne stlačila na nič.
                Nie menej: floor riadku mriežky nedrží tabuľka (oba grid
                itemy majú `min-h-0`), ale `ZakazkaSummary` — je `shrink-0`
                a s cenou a hlavičkou rolety má ~150 px. Pod 160 px sa teda
                na tabuľke nezíska nič (KOLO 63). */}
              {/* Hrany, ktoré dosku neolepia (KOLO 135) */}
              {zamok ? null : (
                <PasHran varianta={activeVarianta} onVyber={setVybraneDielce} />
              )}
              {/* Rezanie sa zmenilo po optimalizácii v Cut Rite (KOLO 153) —
                  posiela rozpočtár (KOLO 231) */}
              {prava.nastavenia_vyroby ? (
                <PasCutrite zakazkaId={zakazkaId} varianta={activeVarianta} />
              ) : null}
              <div className="min-h-[160px] flex-1 overflow-hidden">
                <ZakazkaFlatTable
                  zakazkaId={zakazkaId}
                  varianta={activeVarianta}
                  pamatajZbalenie
                  rozmeryBezHran={zakazka.rozmery_bez_hran ?? false}
                  zakazkaCislo={zakazka.cislo_zakazky ?? null}
                  selectedDielecId={selectedDielecId}
                  onSelectDielec={(id) => {
                    setSelectedDielecId(id);
                    setVybranaZostavaId(null);
                    setVybraneZostavy(new Set());
                  }}
                  selectedZostavaId={vybranaZostavaId}
                  onSelectZostava={(id) => {
                    setVybranaZostavaId(id);
                    setSelectedDielecId(null);
                  }}
                  onAddDielec={(zostavaId) => void pridajDielec(zostavaId)}
                  onAddKovanie={pridajKovanie}
                  onAddCenovka={pridajCenovku}
                  onAddVlastna={pridajVlastnu}
                  onAddZostava={(po, doCelku) => {
                    setZostavaPo(po);
                    setZostavaDoCelku(doCelku ?? null);
                    setZostavaDialogOpen(true);
                  }}
                  vybraneZostavy={vybraneZostavy}
                  onVybraneZostavyChange={zamok ? undefined : setVybraneZostavy}
                  focusDielecId={focusDielecId}
                  onFocusHandled={() => setFocusDielecId(null)}
                  vybraneDielce={vybraneDielce}
                  onVybraneChange={zamok ? undefined : setVybraneDielce}
                  zamok={zamok}
                  odhalit={odhalit}
                />
              </div>
            </div>

            {/* Bočný panel — cena + taby (Sloty/Hrany/Kovania/Rezanie).
              Po zafixovaní layoutu už nepotrebuje sticky ani offset:
              berie plnú výšku stĺpca a nescrolluje. */}
            <BocnyPanel
              zakazka={zakazka}
              varianta={activeVarianta}
              sloty={activeSloty}
              pouziteSlotIds={pouziteSlotIds}
              onOpenSklo={() => setSkloDialogOpen(true)}
              onOznac={oznacVTabulke}
              zamok={zamok}
            />
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Zákazka nemá žiadny aktívny výpočet.
        </p>
      )}

      <PremenovatVariantuDialog
        zakazkaId={zakazkaId}
        varianta={premenovat}
        onOpenChange={(o) => !o && setPremenovat(null)}
      />
      <VariantaCreateDialog
        zakazkaId={zakazkaId}
        variants={varianty}
        rezim={rezimNovej}
        zakazkaNazov={zakazka.nazov ?? ""}
        cisloZ={zakazka.cislo_z ?? null}
        jeVypocet={zakazka.cislo_zakazky === zakazka.vypocet_cislo}
        open={variantDialogOpen}
        onOpenChange={setVariantDialogOpen}
      />
      <PripojitDialog
        zakazka={pripojitStrana ? zakazka : null}
        strana={pripojitStrana ?? "vyroba"}
        onOpenChange={(open) => {
          if (!open) setPripojitStrana(null);
        }}
        onPripojene={(id) => {
          if (id !== zakazkaId) navigate(`/zakazky/${id}`);
        }}
      />
      <TopSolidDoZakazkyDialog
        zakazkaId={zakazkaId}
        maVyrobnu={maVyrobnu}
        cisloZ={zakazka.cislo_z ?? null}
        open={topsolidDialogOpen}
        onOpenChange={setTopsolidDialogOpen}
        onImported={(variantaId) => setActiveVariantaId(variantaId)}
      />

      {activeVarianta ? (
        <ZostavaCreateDialog
          zakazkaId={zakazkaId}
          variantaId={activeVarianta.id}
          existingZostavy={(activeVarianta.zostavy ?? []) as Zostava[]}
          poZostave={zostavaPo}
          doCelku={zostavaDoCelku}
          celky={(activeVarianta.celky ?? []) as Celok[]}
          open={zostavaDialogOpen}
          onOpenChange={setZostavaDialogOpen}
        />
      ) : null}

      {novyCelokOpen && activeVarianta ? (
        <NovyCelokDialog
          zostavy={vybraneNaozaj}
          ukladam={vytvorCelok.isPending}
          onClose={() => setNovyCelokOpen(false)}
          onVytvor={(nazov, zostavaIds) =>
            vytvorCelok.mutate(
              { nazov, zostava_ids: zostavaIds },
              {
                onSuccess: () => {
                  setNovyCelokOpen(false);
                  toast({ title: `Celok ${nazov} pridaný` });
                },
                onError: (err) =>
                  toast({
                    variant: "destructive",
                    title: "Celok sa nepodarilo pridať",
                    description: extractErrorMessage(err),
                  }),
              },
            )
          }
        />
      ) : null}

      {dielecCreateContext?.rezim === "vlastna" && activeVarianta ? (
        <VlastnaPolozkaDialog
          zakazkaId={zakazkaId}
          zostavaId={dielecCreateContext.zostavaId}
          zostavaKod={dielecCreateContext.zostavaKod}
          existingDielce={
            ((activeVarianta.zostavy ?? []) as Zostava[]).find(
              (z) => z.id === dielecCreateContext.zostavaId,
            )?.dielce ?? []
          }
          dalsieZostavy={dalsieZostavyPridania(dielecCreateContext.dalsie)}
          open
          onOpenChange={(o) => {
            if (!o) setDielecCreateContext(null);
          }}
        />
      ) : null}

      {dielecCreateContext && dielecCreateContext.rezim !== "vlastna" && activeVarianta ? (
        <DielecCreateDialog
          zakazkaId={zakazkaId}
          zostavaId={dielecCreateContext.zostavaId}
          zostavaKod={dielecCreateContext.zostavaKod}
          existingDielce={
            ((activeVarianta.zostavy ?? []) as Zostava[]).find(
              (z) => z.id === dielecCreateContext.zostavaId,
            )?.dielce ?? []
          }
          sloty={activeSloty}
          rezim={dielecCreateContext.rezim}
          dalsieZostavy={dalsieZostavyPridania(dielecCreateContext.dalsie)}
          rozmeryBezHran={zakazka.rozmery_bez_hran ?? false}
          open={!!dielecCreateContext}
          onOpenChange={(o) => {
            if (!o) setDielecCreateContext(null);
          }}
        />
      ) : null}

      {(skloDialogOpen || skloEditTarget) && activeVarianta ? (
        <SkloCreateDialog
          zakazkaId={zakazkaId}
          zostavy={(activeVarianta.zostavy ?? []) as Zostava[]}
          defaultZostavaId={selectedZostavaId}
          open
          onOpenChange={(o) => {
            if (!o) {
              setSkloDialogOpen(false);
              setSkloEditTarget(null);
            }
          }}
          editDielec={skloEditTarget}
          editSucasti={skloEditTarget ? selectedSucasti : []}
        />
      ) : null}

      {komponentDialogOpen && activeVarianta ? (
        <KomponentDialog
          zakazkaId={zakazkaId}
          variantaId={activeVarianta.id}
          zostavy={(activeVarianta.zostavy ?? []) as Zostava[]}
          sloty={activeSloty}
          selectedZostavaId={selectedZostavaId}
          open={komponentDialogOpen}
          onOpenChange={setKomponentDialogOpen}
        />
      ) : null}
    </div>
  );
}

function BackLink(): JSX.Element {
  return (
    <Button variant="ghost" size="sm" asChild className="-ml-3">
      <Link to={adresaZoznamu()} title="Späť na zoznam zákaziek">
        <ArrowLeft className="h-4 w-4" />
        Späť na zoznam
      </Link>
    </Button>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-4">
      <BackLink />
      <Skeleton className="h-10 w-1/2" />
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}

/**
 * Panel nástrojov rozpočtu tovaru (KOLO 241, návrh T1): Pridať ▾ (tovar
 * z cenníka, vlastná položka — do vybranej zostavy, inak prvej) a Regál CRC
 * / Upraviť regál; rozpočtár (tovar len na čítanie) má len Pozrieť regál.
 * Do KOLA 240 boli tieto tlačidlá v modrom páse nad tabuľkou tovaru.
 */
function TlacidlaTovaru({
  varianta,
  lenCitanie,
  zostavaId,
  onTovar,
  onVlastna,
  onRegal,
}: {
  varianta: Varianta | null;
  lenCitanie: boolean;
  zostavaId: string | null;
  onTovar: (z: Zostava) => void;
  onVlastna: (zostavaId: string) => void;
  onRegal: () => void;
}): JSX.Element | null {
  if (!varianta) return null;
  const zostavy = (varianta.zostavy ?? []) as Zostava[];
  const ciel = zostavy.find((z) => z.id === zostavaId) ?? zostavy[0] ?? null;
  const maRegal = Boolean(varianta.ma_regal);
  if (lenCitanie) {
    return maRegal ? (
      <Button size="sm" variant="outline" onClick={onRegal}>
        <LayoutGrid className="h-4 w-4" />
        Pozrieť regál
      </Button>
    ) : null;
  }
  return (
    <>
      <PridatTovarMenu
        cielPopis={ciel ? `do zostavy ${ciel.kod}` : null}
        onTovar={() => ciel && onTovar(ciel)}
        onVlastna={() => ciel && onVlastna(ciel.id)}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={onRegal}
        aria-label={maRegal ? "Upraviť regál" : "Regál CRC"}
        title="Konfigurátor regálu CRC — zostavy regálu do tovaru, diely Tandemu rozpočtárovi"
      >
        <LayoutGrid className="h-4 w-4" />
        {/* na mobile len ikona — tri tlačidlá sa do 390 px nezmestia */}
        <span className="hidden sm:inline">{maRegal ? "Upraviť regál" : "Regál CRC"}</span>
      </Button>
    </>
  );
}

